/**
 * Product Options Compare-At Price Sync
 *
 * When Hulk Product Options (or similar apps) dynamically update the sale price
 * on option change, this script updates the compare-at price accordingly.
 *
 * Fixed amount off (default): discount = compare_at - price
 *   upgraded_compare_at = new_price + discount
 *
 * Percentage off: add data-discount-type="percentage" to price container
 *   upgraded_compare_at = new_price / (base_price / base_compare_at)
 */

(function () {
  'use strict';

  const SELECTORS = {
    priceContainer: '[data-product-options-compare-at]',
    salePrice: '.price__sale .price-item--sale',
    compareAtPrice: '.price__sale s.price-item--regular'
  };

  /**
   * Parse displayed price string to cents.
   * Handles UK/US (1,054.00) and EU (1.054,00) formats correctly.
   */
  function parsePriceToCents(text) {
    if (!text || typeof text !== 'string') return null;
    const cleaned = text.replace(/[^\d.,\-]/g, '').trim();
    if (!cleaned) return null;

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    let normalized;
    if (lastComma > lastDot) {
      // EU format: 1.054,00 — comma is decimal separator
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // UK/US format: 1,054.00 — dot is decimal, comma is thousands
      normalized = cleaned.replace(/,/g, '');
    }

    const num = parseFloat(normalized);
    return isNaN(num) ? null : Math.round(num * 100);
  }

  /**
   * Format cents as currency string using shop's locale/currency
   */
  function formatMoney(cents, currency = 'GBP') {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }

  /**
   * Calculate compare-at from new sale price.
   * Fixed amount: discount = compare_at - price, new_compare_at = new_price + discount
   * Percentage: new_compare_at = new_price / (base_price / base_compare_at)
   */
  function calculateCompareAt(basePrice, baseCompareAt, newPriceCents, discountType) {
    if (!basePrice || !baseCompareAt || baseCompareAt <= basePrice) return null;

    if (discountType === 'percentage') {
      const discountRatio = basePrice / baseCompareAt;
      return Math.round(newPriceCents / discountRatio);
    }

    // Fixed amount off (default)
    const discountAmount = baseCompareAt - basePrice;
    return newPriceCents + discountAmount;
  }

  /**
   * Sync compare-at display when sale price has changed
   */
  function syncCompareAt(priceEl) {
    const basePrice = parseInt(priceEl.dataset.basePrice, 10);
    const baseCompareAt = parseInt(priceEl.dataset.baseCompareAt, 10);
    const currency = priceEl.dataset.currency || 'GBP';
    const discountType = priceEl.dataset.discountType || 'fixed';

    if (!basePrice || !baseCompareAt || baseCompareAt <= basePrice) return;

    const saleEl = priceEl.querySelector(SELECTORS.salePrice);
    const compareEl = priceEl.querySelector(SELECTORS.compareAtPrice);

    if (!saleEl || !compareEl) return;

    const displayedText = (saleEl.textContent || '').trim();
    const newPriceCents = parsePriceToCents(displayedText);

    if (newPriceCents === null || newPriceCents <= 0) return;

    const newCompareAtCents = calculateCompareAt(basePrice, baseCompareAt, newPriceCents, discountType);
    if (newCompareAtCents === null || newCompareAtCents <= newPriceCents) return;

    const formattedCompareAt = formatMoney(newCompareAtCents, currency);

    if (compareEl.textContent !== formattedCompareAt) {
      compareEl.textContent = formattedCompareAt;
    }
  }

  /**
   * Set up MutationObserver to detect when options app updates the price
   */
  function observePriceChanges(priceEl) {
    const saleEl = priceEl.querySelector(SELECTORS.salePrice);
    if (!saleEl) return;

    const observer = new MutationObserver(function () {
      syncCompareAt(priceEl);
    });

    observer.observe(priceEl, {
      characterData: true,
      characterDataOldValue: true,
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Initialize observers for all product-option price blocks
   */
  function initPriceObservers(container = document) {
    const priceBlocks = container.querySelectorAll(SELECTORS.priceContainer);
    priceBlocks.forEach(function (priceEl) {
      if (priceEl._productOptionsCompareAtObserved) return;

      const saleEl = priceEl.querySelector(SELECTORS.salePrice);
      if (!saleEl) return;

      observePriceChanges(priceEl);
      priceEl._productOptionsCompareAtObserved = true;

      // Initial sync in case options app already modified the price
      requestAnimationFrame(function () {
        syncCompareAt(priceEl);
      });
    });
  }

  /**
   * Re-init when product-info replaces the price block (variant change)
   */
  function setupProductInfoWatcher() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const watcher = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          initPriceObservers(productInfo);
          break;
        }
      }
    });

    watcher.observe(productInfo, { childList: true, subtree: true });
  }

  /**
   * Listen for variant change (fresh HTML with new base prices)
   */
  function setupVariantChangeListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : {};
    const variantChange = events.variantChange || 'variant-change';

    subscribe(variantChange, function () {
      document.querySelectorAll(SELECTORS.priceContainer).forEach(function (el) {
        delete el._productOptionsCompareAtObserved;
      });
      setTimeout(initPriceObservers, 50);
    });
  }

  /**
   * Listen for option/form changes (options app may update price after)
   */
  function setupFormChangeListener() {
    document.addEventListener('change', function (e) {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      const productInfo = form.closest('product-info');
      if (productInfo) {
        setTimeout(function () {
          initPriceObservers(productInfo);
        }, 300);
      }
    });
  }

  function init() {
    if (!document.querySelector(SELECTORS.priceContainer)) return;

    initPriceObservers();
    setupProductInfoWatcher();
    setupVariantChangeListener();
    setupFormChangeListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support for dynamic section rendering (e.g. theme editor, quick view)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('product-info:loaded', init);
})();
